import { segmentation } from '@thecompaniesapi/shared/client'
import cloneDeep from 'lodash/cloneDeep'

export default function useSegmentation() {
  const { t } = useI18n()
  const localePath = useLocalePath()
  const route = useRoute()
  const router = useRouter()
  const listsStore = useListsStore()
  const userStore = useUserStore()

  const checkIfConditionsAreSimilar = (condition1?: SegmentationCondition, condition2?: SegmentationCondition) => {
    if (!condition1 || !condition2) {
      return false
    }

    return condition1.attribute === condition2.attribute
      && condition1.operator === condition2.operator
      && condition1.sign === condition2.sign
      && condition1.values.length === condition2.values.length
      && condition1.values.sort().join(',') === condition2.values.sort().join(',')
  }

  const checkIfConditionsMatchWithUrl = (conditions: LocationQuery) => {
    // Check if we have the same number of conditions
    const conditionsCount = getConditionsCount(conditions)
    const routeQueryConditionsCount = getConditionsCount(route?.query)

    // Check if we have the same number of conditions
    if (conditionsCount !== routeQueryConditionsCount) {
      return false
    }

    // Make sure all conditions equal to each other
    for (const conditionKey in conditions) {
      if (`${conditions[conditionKey]}`.split(segmentation.separator).length > 3 && conditions[conditionKey] !== route?.query[conditionKey]) {
        return false
      }
    }

    return true
  }

  const checkIfQueriesMatchWithEachOther = (params: {
    query1: SegmentationCondition[]
    query2: SegmentationCondition[]
  }) => {
    const normalizedQuery1 = normalizeQuery(params.query1)
    const normalizedQuery2 = normalizeQuery(params.query2)

    // Check if we have the same number of conditions
    if (normalizedQuery1.length !== normalizedQuery2.length) {
      return false
    }

    // Make sure all conditions equal to each other
    for (const [index, condition] of normalizedQuery1.entries()) {
      if (!checkIfConditionsAreSimilar(condition, normalizedQuery2[index])) {
        return false
      }
    }

    return true
  }

  const checkIfSimilarQueriesMatchWithEachOther = (params: {
    domains1: string[]
    domains2: string[]
  }) => {
    // Security check
    if (!Array.isArray(params.domains1) || !Array.isArray(params.domains2)) {
      return false
    }

    // Nomalize domains
    const normalizedDomains1 = params.domains1.map(domain => domain.toLowerCase().trim()).sort()
    const normalizedDomains2 = params.domains2.map(domain => domain.toLowerCase().trim()).sort()

    // Check if we have the same number of domains
    if (normalizedDomains1.length !== normalizedDomains2.length) {
      return false
    }

    // Make sure domains equal to each other
    for (const domain of normalizedDomains1) {
      if (!normalizedDomains2.includes(domain)) {
        return false
      }
    }

    return true
  }

  const checkIfQueryNotEmpty = (query: SegmentationCondition[]) => {
    // Make sure at least one value is set
    for (const condition of query) {
      if (condition.attribute === 'ai.search' && typeof condition.values?.[0] === 'string' && !condition.values[0].length) {
        return false
      }

      if (condition.values.length) {
        return true
      }
    }

    return false
  }

  const checkIfSimilarDomainsMatchWithUrl = (similarDomains: string[]) => {
    const domainsFromUrls = route?.query.domains ? `${route?.query.domains}`.split(',') : []

    // Check if we have the same number of similar domains
    if (domainsFromUrls.length !== similarDomains.length) {
      return false
    }

    // Make sure domains equal to each other
    for (const similarDomain of similarDomains) {
      if (!domainsFromUrls.includes(similarDomain)) {
        return false
      }
    }

    return true
  }

  const createCondition = function (params?: {
    attribute?: SegmentationAttribute
    blockedOperator?: boolean
    operator?: SegmentationOperator
    query?: SegmentationCondition[]
    sign?: SegmentationSign
    values?: string[]
  }): SegmentationCondition {
    // Find the next unused attribute for the query
    if (params?.query?.length) {
      const optionsForAttributes = getOptionsForAttributes()
      const currentAttributes = params.query.map(condition => condition.attribute)
      const forbiddenAttributes: SegmentationAttribute[] = ['about.industries']

      for (const option of optionsForAttributes) {
        if (!currentAttributes.includes(option.value) && !forbiddenAttributes.includes(option.value)) {
          params.attribute = option.value
          break
        }
      }
    }

    return {
      attribute: params?.attribute || 'about.industries',
      blockedOperator: params?.attribute ? segmentation?.attributesWithBlockedOperator?.includes(params.attribute) : false,
      operator: params?.operator || 'or',
      sign: params?.sign || 'equals',
      values: params?.values?.filter(Boolean) || [],
    }
  }

  const generateNameFromQuery = (query: SegmentationCondition[]) => {
    let name = ''

    const queryValues = query[0].values.map((value) => {
      return changeCase.noCase(String(value))
    })

    for (const [index, value] of queryValues.entries()) {
      if (index === 0) {
        name += value
      }
      else if (index <= 3) {
        name += ` ${value}`
      }
      else {
        name += '…'
        break
      }
    }

    return name
  }

  const getCompaniesSearchUrl = (params: {
    attribute: SegmentationAttribute
    operator?: SegmentationOperator
    sign?: SegmentationSign
    value?: string
  }) => {
    if (!userStore.user) {
      return localePath('auth-login')
    }

    return localePath({
      name: 'companies',
      query: {
        c1: `${params.attribute}${segmentation.separator}${params.value}${segmentation.separator}${params.sign || 'equals'}${segmentation.separator}${params.operator || 'or'}`,
        page: '1',
      },
    })
  }

  const getConditionsCount = (conditions: LocationQuery) => {
    const conditionRegex = /^(c|p)\d$/

    return Object.keys(conditions).filter((conditionKey) => {
      // Make sure the condition is c|p{number} and that a value is defined
      return conditionRegex.test(conditionKey) && `${conditions[conditionKey]}`.split(segmentation.separator).length > 3
    }).length
  }

  const getConditionsFromQuery = (query: SegmentationCondition[]) => {
    const conditions: LocationQuery = {}

    if (query && query.length) {
      for (let index = 0; index < query.length; index++) {
        const condition = query[index]
        const prefix = 'c'

        // Skip empty conditions
        if (!condition.values.length) {
          continue
        }

        conditions[`${prefix}${index + 1}`] = [
          condition.attribute,
          ...condition.values,
          condition.sign,
          condition.operator,
        ].join(segmentation.separator)
      }
    }

    return conditions
  }

  const getConditionsFromUrl = () => {
    const query = getQueryFromUrl()
    const conditions = getConditionsFromQuery(query)

    return conditions
  }

  const getIndexForAttribute = (attribute: SegmentationAttribute) => {
    let index: SuggestionsIndices | undefined

    if (attribute === 'apps') {
      index = 'apps'
    }
    else if (attribute === 'about.businessType') {
      index = 'business-types'
    }
    else if (attribute === 'about.industry') {
      index = 'industries-main'
    }
    else if (attribute === 'about.industries') {
      index = 'industries'
    }
    else if (attribute === 'about.totalEmployees') {
      index = 'total-employees'
    }
    else if (attribute === 'analytics.monthlyVisitors') {
      index = 'monthly-visitors'
    }
    else if (attribute === 'codes.naics') {
      index = 'industries-naics'
    }
    else if (attribute === 'codes.sic') {
      index = 'industries-sic'
    }
    else if (attribute === 'contacts') {
      index = 'contacts'
    }
    else if (attribute === 'domain.tld') {
      index = 'tlds'
    }
    else if (attribute === 'finances.revenue') {
      index = 'revenues'
    }
    else if (attribute === 'finances.stockExchange') {
      index = 'stock-exchanges'
    }
    else if (attribute === 'locations.headquarters.city.code') {
      index = 'cities'
    }
    else if (attribute === 'locations.headquarters.county.code') {
      index = 'counties'
    }
    else if (attribute === 'locations.headquarters.state.code') {
      index = 'states'
    }
    else if (attribute === 'locations.headquarters.country.code') {
      index = 'countries'
    }
    else if (attribute === 'locations.headquarters.continent.code') {
      index = 'continents'
    }
    else if (attribute === 'socials') {
      index = 'social-networks'
    }
    else if (attribute === 'technologies.active') {
      index = 'technologies'
    }

    return index
  }

  const getLabelForAttribute = (attribute: SegmentationAttribute) => {
    return t(`datapoints.attributes.${attribute.replaceAll('.', '-').replace('-code', '')}`)
  }

  const getOptionsForAttributes = (attributesToExclude: SegmentationAttribute[] = []) => {
    const attributes: SegmentationAttribute[] = [
      'about.businessType',
      'about.industry',
      'about.industries',
      'about.totalEmployees',
      'about.yearFounded',
      'ai.search',
      'analytics.monthlyVisitors',
      'apps',
      'codes.naics',
      'codes.sic',
      'contacts',
      'domain.tld',
      'finances.revenue',
      'finances.stockExchange',
      'locations.headquarters.city.code',
      'locations.headquarters.continent.code',
      'locations.headquarters.country.code',
      'locations.headquarters.county.code',
      'locations.headquarters.state.code',
      'meta.score',
      'meta.syncedAt',
      'socials',
      'technologies.active',
    ]

    if (listsStore.lists.length) {
      attributes.push('meta.listIds')
    }

    return attributes
      .filter(attribute => !attributesToExclude.includes(attribute))
      .map(attribute => ({
        icon: getIconForAttribute(attribute),
        label: getLabelForAttribute(attribute),
        value: attribute,
      }))
  }

  const getQueryFromUrl = (location?: LocationQuery) => {
    const uselessParams = [
      'companyListId',
      'listId',
      'page',
      'search',
    ]

    const query: SegmentationCondition[] = []
    const routeQuery = cloneDeep(location || route?.query)
    const conditionPrefix = 'c'

    for (const routeQueryParam in routeQuery) {
      if (!uselessParams.includes(routeQueryParam) && typeof routeQuery[routeQueryParam] === 'string' && routeQueryParam.startsWith(conditionPrefix)) {
        const values = `${routeQuery[routeQueryParam]}`.split(segmentation.separator)
        const attribute = values.shift() as SegmentationAttribute

        // Compute operator
        let operator: SegmentationOperator = 'and'

        if (segmentation.operators.includes(values[values.length - 1] as SegmentationOperator)) {
          operator = values[values.length - 1] as SegmentationOperator

          values.pop()
        }

        // Compute operator blocked
        let blockedOperator = false

        if (segmentation.attributesWithBlockedOperator.includes(attribute || '')) {
          operator = 'or'
          blockedOperator = true
        }

        // Compute sign
        let sign: SegmentationSign = 'equals'

        if (segmentation.signs.includes(values[values.length - 1] as SegmentationSign)) {
          sign = values[values.length - 1] as SegmentationSign

          values.pop()
        }

        // Create condition and push it to query
        const condition = createCondition({
          attribute,
          blockedOperator,
          operator,
          sign,
          values,
        })

        query.push(condition)
      }
    }

    return query
  }

  const getSimilarQueryFromUrl = (location?: LocationQuery) => {
    let domains: string[] = []

    if (typeof location?.domains === 'string') {
      domains = [location.domains]
    }
    else if (Array.isArray(location?.domains)) {
      domains = location.domains as string[]
    }

    return domains
  }

  const normalizeQuery = (query: SegmentationCondition[]) => {
    return query
      .map(condition => ({
        ...condition,
        values: condition.values.slice().sort(),
      }))
      .sort((current, next) => {
        return current.attribute.localeCompare(next.attribute)
      })
  }

  const removeEmptyConditionsFromQuery = (query: SegmentationCondition[]) => {
    // Security check to avoid errors when query is wrong
    if (!query || !query.length || !Array.isArray(query)) {
      return []
    }

    return query.filter((condition) => {
      return condition.values.length > 0
    })
  }

  const synchronizeUrl = (params: {
    domains?: string[]
    query: SegmentationCondition[]
    route?: RouteLocation
  }) => {
    const companiesConditions = getConditionsFromQuery(params.query)
    const conditionsMatchWithUrl = checkIfConditionsMatchWithUrl(companiesConditions)
    const domainsMatchWithUrl = checkIfSimilarDomainsMatchWithUrl(params.domains || [])

    if (!conditionsMatchWithUrl || !domainsMatchWithUrl) {
      router.push(
        localePath(
          {
            ...(params.route || {}),
            name: params.route?.name || String(route.name).split('_')[0],
            query: {
              ...(params.route?.query || {}),
              ...companiesConditions,
              ...(params.domains?.length && { domains: params.domains }),
            },
          },
        ),
      )
    }
  }

  return {
    checkIfConditionsAreSimilar,
    checkIfConditionsMatchWithUrl,
    checkIfQueriesMatchWithEachOther,
    checkIfQueryNotEmpty,
    checkIfSimilarDomainsMatchWithUrl,
    checkIfSimilarQueriesMatchWithEachOther,
    createCondition,
    generateNameFromQuery,
    getCompaniesSearchUrl,
    getConditionsCount,
    getConditionsFromQuery,
    getConditionsFromUrl,
    getIndexForAttribute,
    getLabelForAttribute,
    getOptionsForAttributes,
    getQueryFromUrl,
    getSimilarQueryFromUrl,
    normalizeQuery,
    removeEmptyConditionsFromQuery,
    segmentation,
    synchronizeUrl,
  }
}
